<template>
    <div>
        <!-- WhatsApp Connection Status Alert -->
        <v-row v-if="!whatsappConnected">
            <v-col cols="12">
                <v-alert type="warning" dense class="rounded-lg">
                    WhatsApp desconectado. Algumas funções podem estar indisponíveis.
                </v-alert>
            </v-col>
        </v-row>

        <v-form ref="form" v-model="valid">
            <v-row>
                <!-- Global Channel Settings -->
                <v-col cols="12">
                    <v-card id="tour-global-controls" class="rounded-lg mb-4" elevation="1">
                        <v-card-title class="py-3">
                            <v-icon left color="primary">mdi-tune</v-icon>
                            Preferências Globais
                        </v-card-title>
                        <v-divider></v-divider>
                        <v-card-text class="py-4">
                            <v-row align="center">
                                <v-col cols="12" md="6" class="d-flex align-center justify-space-between">
                                    <div class="d-flex align-center">
                                        <v-icon color="grey darken-1" class="mr-3">mdi-cellphone</v-icon>
                                        <div>
                                            <div class="subtitle-1 font-weight-medium text--primary">Notificações App</div>
                                            <div class="caption grey--text">Enviar push notifications para o app</div>
                                        </div>
                                    </div>
                                    <v-switch
                                        v-model="settings.app_enabled"
                                        color="primary"
                                        hide-details
                                        inset
                                    ></v-switch>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-divider class="d-md-none my-2"></v-divider>
                                </v-col>
                                <v-col cols="12" md="6" class="d-flex align-center justify-space-between">
                                    <div class="d-flex align-center">
                                        <v-icon color="grey darken-1" class="mr-3">mdi-whatsapp</v-icon>
                                        <div>
                                            <div class="subtitle-1 font-weight-medium text--primary">Notificações WhatsApp</div>
                                            <div class="caption grey--text">Enviar mensagens automáticas via Zap</div>
                                        </div>
                                    </div>
                                    <v-switch
                                        v-model="settings.whatsapp_enabled"
                                        color="success"
                                        :disabled="!whatsappConnected"
                                        hide-details
                                        inset
                                    ></v-switch>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-col>

                <!-- Appointment Notifications -->
                <v-col id="tour-templates-section" cols="12">
                    <settings-section-card
                        title="Agendamentos"
                        icon="mdi-calendar-check"
                        :variables="appointmentVariables"
                        :preview-data="dummyAppointmentData"
                        :settings="settings"
                        :whatsapp-enabled="settings.whatsapp_enabled"
                        section-type="appointment"
                        @update="updateSettings"
                    />
                </v-col>

                <!-- Payment Notifications -->
                <v-col cols="12">
                    <settings-section-card
                        title="Pagamentos"
                        icon="mdi-cash-multiple"
                        :variables="paymentVariables"
                        :preview-data="dummyPaymentData"
                        :settings="settings"
                        :whatsapp-enabled="settings.whatsapp_enabled"
                        section-type="payment"
                        @update="updateSettings"
                    />
                </v-col>

                <!-- Action Buttons -->
                <v-col cols="12">
                    <v-card class="rounded-lg grey lighten-4" elevation="1">
                        <v-card-actions class="pa-4">
                            <v-btn
                                text
                                color="grey darken-1"
                                @click="resetToDefaults"
                            >
                                <v-icon left>mdi-backup-restore</v-icon>
                                Restaurar Padrões
                            </v-btn>
                            <v-spacer></v-spacer>
                            <v-btn
                                color="primary"
                                x-large
                                elevation="2"
                                :loading="loading"
                                :disabled="!valid"
                                @click="saveSettings"
                                class="px-8"
                            >
                                <v-icon left>mdi-content-save-check</v-icon>
                                Salvar Alterações
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-col>
            </v-row>
        </v-form>

        <v-snackbar v-model="snackbar.show" :color="snackbar.color" top right>
            {{ snackbar.text }}
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="snackbar.show = false">Fechar</v-btn>
            </template>
        </v-snackbar>
    </div>
</template>

<script>
import NotificationService from '../services/notifications';
import SettingsSectionCard from './SettingsSectionCard.vue';

export default {
    name: 'NotificationSettings',
    components: { SettingsSectionCard },
    data: () => ({
        valid: false,
        loading: false,
        whatsappConnected: false,
        settings: {
            app_enabled: true,
            whatsapp_enabled: false,
            app_message_24h: '',
            app_message_1h: '',
            whatsapp_message_24h: '',
            whatsapp_message_1h: '',
            payment_app_message_1day: '',
            payment_app_message_day: '',
            payment_whatsapp_message_1day: '',
            payment_whatsapp_message_day: ''
        },
        defaultSettings: {
            app_message_24h: 'Olá {{customer_name}}! Lembrete: você tem uma sessão de {{service_name}} agendada para amanhã, {{session_date}} às {{session_time}}.',
            app_message_1h: 'Olá {{customer_name}}! Sua sessão de {{service_name}} com {{doctor_name}} começará em 1 hora ({{session_time}}). Até já!',
            whatsapp_message_24h: 'Olá {{customer_name}}! 👋\n\nLembrete: você tem uma sessão de {{service_name}} agendada para amanhã.\n\n📅 Data: {{session_date}}\n🕐 Horário: {{session_time}}\n\nNos vemos em breve!',
            whatsapp_message_1h: 'Olá {{customer_name}}! 👋\n\nSua sessão de {{service_name}} com {{doctor_name}} começará em 1 hora.\n\n🕐 Horário: {{session_time}}\n\nAté já! 😊',
            payment_app_message_1day: 'Olá {{customer_name}}! Lembrete: o pagamento de {{service_name}} no valor de {{payment_value}} vence amanhã ({{payment_date}}).',
            payment_app_message_day: 'Olá {{customer_name}}! O pagamento de {{service_name}} no valor de {{payment_value}} vence hoje ({{payment_date}}).',
            payment_whatsapp_message_1day: 'Olá {{customer_name}}! 👋\n\nLembrete de pagamento:\n\n💰 Valor: {{payment_value}}\n📅 Vencimento: {{payment_date}} (amanhã)\n🏥 Serviço: {{service_name}}\n\nObrigado!',
            payment_whatsapp_message_day: 'Olá {{customer_name}}! 👋\n\nLembrete: seu pagamento vence hoje!\n\n💰 Valor: {{payment_value}}\n📅 Vencimento: {{payment_date}}\n🏥 Serviço: {{service_name}}\n\nObrigado!'
        },
        snackbar: { show: false, text: '', color: 'success' },
        
        // Definitions for Variables and Dummy Data
        appointmentVariables: [
            { key: 'customer_name', label: 'Nome Cliente' },
            { key: 'session_date', label: 'Data' },
            { key: 'session_time', label: 'Horário' },
            { key: 'doctor_name', label: 'Profissional' },
            { key: 'service_name', label: 'Serviço' }
        ],
        paymentVariables: [
            { key: 'customer_name', label: 'Nome Cliente' },
            { key: 'payment_date', label: 'Vencimento' },
            { key: 'payment_value', label: 'Valor' },
            { key: 'doctor_name', label: 'Profissional' },
            { key: 'service_name', label: 'Serviço' }
        ],
        dummyAppointmentData: {
            customer_name: 'Nome do Cliente',
            session_date: '25/10/2023',
            session_time: '14:30',
            doctor_name: 'Nome do Profissional',
            service_name: 'Nome do Serviço'
        },
        dummyPaymentData: {
            customer_name: 'Nome do Cliente',
            payment_date: '25/10/2023',
            payment_value: 'R$ 150,00',
            doctor_name: 'Nome do Profissional',
            service_name: 'Nome do Serviço'
        }
    }),
    computed: {
        whatsappRules() {
            return [
                v => !this.settings.whatsapp_enabled || !!v || 'Mensagem obrigatória quando WhatsApp está habilitado'
            ];
        }
    },
    async created() {
        await this.checkWhatsappConnection();
        await this.loadSettings();
    },
    methods: {
        updateSettings(newSettings) {
            this.settings = newSettings;
        },
        async checkWhatsappConnection() {
            try {
                const response = await NotificationService.checkWhatsappStatus();
                this.whatsappConnected = response.data.status === 'CONNECTED';
            } catch (error) {
                this.whatsappConnected = false;
                console.error('Error checking WhatsApp status:', error);
            }
        },
        async loadSettings() {
            this.loading = true;
            try {
                const response = await NotificationService.getNotificationSettings();
                const data = response.data;
                this.settings = {
                    app_enabled: data.app_enabled ?? true,
                    whatsapp_enabled: data.whatsapp_enabled ?? false,
                    app_message_24h: data.app_message_24h || this.defaultSettings.app_message_24h,
                    app_message_1h: data.app_message_1h || this.defaultSettings.app_message_1h,
                    whatsapp_message_24h: data.whatsapp_message_24h || this.defaultSettings.whatsapp_message_24h,
                    whatsapp_message_1h: data.whatsapp_message_1h || this.defaultSettings.whatsapp_message_1h,
                    payment_app_message_1day: data.payment_app_message_1day || this.defaultSettings.payment_app_message_1day,
                    payment_app_message_day: data.payment_app_message_day || this.defaultSettings.payment_app_message_day,
                    payment_whatsapp_message_1day: data.payment_whatsapp_message_1day || this.defaultSettings.payment_whatsapp_message_1day,
                    payment_whatsapp_message_day: data.payment_whatsapp_message_day || this.defaultSettings.payment_whatsapp_message_day
                };
            } catch (error) {
                if (error.response?.status === 404) {
                    this.resetToDefaults();
                } else {
                    this.showSnackbar('Erro ao carregar configurações', 'error');
                }
            } finally {
                this.loading = false;
            }
        },
        async saveSettings() {
            if (!this.$refs.form.validate()) return;
            if (this.settings.whatsapp_enabled && !this.whatsappConnected) {
                this.showSnackbar('WhatsApp não está conectado. Conecte o WhatsApp antes de habilitar notificações.', 'error');
                return;
            }
            this.loading = true;
            try {
                await NotificationService.saveNotificationSettings(this.settings);
                this.showSnackbar('Configurações salvas com sucesso!', 'success');
            } catch (error) {
                this.showSnackbar('Erro ao salvar configurações', 'error');
                console.error(error);
            } finally {
                this.loading = false;
            }
        },
        resetToDefaults() {
            this.$swal({
                title: 'Restaurar Padrões?',
                text: 'Isso irá substituir todas as mensagens personalizadas pelas mensagens padrão.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Sim, restaurar',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#f44336'
            }).then((result) => {
                if (result.isConfirmed) {
                    this.settings = { ...this.defaultSettings };
                    this.showSnackbar('Mensagens restauradas para os padrões', 'info');
                }
            });
        },
        showSnackbar(text, color) {
            this.snackbar.text = text;
            this.snackbar.color = color;
            this.snackbar.show = true;
        }
    }
};
</script>

<style scoped>
/* Add transition for smooth toggle */
.v-btn-toggle {
    transition: all 0.3s ease;
}
</style>
